import { alpha, Card, CardContent, CardHeader, Typography, Checkbox, List, ListItem } from '@mui/material';
import { Project, Workflow, WorkflowNode } from 'api/model';
import { getNodeConfig } from 'api/workflows-steps/formSchema';
import { NodeRowCard } from 'views/flow/form/Form.styles';

type NodeItemProps = {
  node: WorkflowNode;
  selectable?: boolean;
  isSelected?: boolean;
  onSelectionChange?: (nodeId: string, selected: boolean) => void;
};

export const ToastNodeItem = ({ node, selectable = false, isSelected, onSelectionChange }: NodeItemProps) => {
  const { color, icon } = getNodeConfig(node.type)!;

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSelectionChange?.(node.id, event.target.checked);
  };

  return (
    <ListItem className="flex items-center gap-2 my-1 p-0">
      {selectable && <Checkbox checked={isSelected} onChange={handleCheckboxChange} size="small" />}
      <NodeRowCard color={color} size={4}>
        {icon}
      </NodeRowCard>
      <Typography variant="body2">{node.name}</Typography>
    </ListItem>
  );
};

type WorkflowCardProps = {
  workflow: Workflow;
  project?: Project;
  nodeIds: Set<string>;
  onNavigate: (workflow: Workflow) => void;
  selectable?: boolean;
  selectedNodes?: Set<string>;
  onNodeSelectionChange?: (nodeIds: string[], selected: boolean) => void;
};

const WorkflowCard = ({
  workflow,
  project,
  nodeIds,
  onNavigate,
  selectable = false,
  selectedNodes,
  onNodeSelectionChange,
}: WorkflowCardProps) => {
  const workflowNodeIds = workflow.nodes.filter((node) => nodeIds.has(node.id)).map((node) => node.id);
  const selectedWorkflowNodes = workflowNodeIds.filter((id) => selectedNodes?.has(id));

  const isAllSelected = workflowNodeIds.length > 0 && selectedWorkflowNodes.length === workflowNodeIds.length;
  const isIndeterminate = selectedWorkflowNodes.length > 0 && selectedWorkflowNodes.length < workflowNodeIds.length;

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onNodeSelectionChange?.(workflowNodeIds, event.target.checked);
  };

  return (
    <Card
      variant="outlined"
      className="mb-3"
      sx={{
        borderRadius: '12px',
        borderColor: 'divider',
        backgroundColor: (t) => alpha(t.palette.primary.main, 0.04),
        backgroundImage: 'none',
      }}
    >
      <CardHeader
        action={
          selectable && (
            <Checkbox
              checked={isAllSelected}
              indeterminate={isIndeterminate}
              onChange={handleSelectAllChange}
              size="small"
            />
          )
        }
        title={
          <Typography
            variant="subtitle1"
            className="cursor-pointer font-semibold hover:underline"
            sx={{ color: 'info.main' }}
            onClick={() => onNavigate(workflow)}
          >
            {workflow.name}
          </Typography>
        }
        subheader={
          <Typography variant="caption" color="text.secondary">
            Project: {project?.name ?? 'Unknown'}
          </Typography>
        }
        className="pb-0"
      />
      <CardContent>
        <List>
          {workflow.nodes
            .filter((node) => nodeIds.has(node.id))
            .map((node) => (
              <ToastNodeItem
                key={node.id}
                node={node}
                selectable={selectable}
                isSelected={selectedNodes?.has(node.id)}
                onSelectionChange={(nodeId, selected) => onNodeSelectionChange?.([nodeId], selected)}
              />
            ))}
        </List>
      </CardContent>
    </Card>
  );
};

export default WorkflowCard;
